import {forwardRef, useMemo} from 'react'
import Link from 'next/link'
import css from 'styled-jsx/css'
import {get} from '@cullylarson/f'
import {classnames} from '@@client/lib/classes'
import {useConfig} from '@@client/lib/hooks'
import {formatDollars} from '@@client/lib/numbers'
import Messages from '@@client/components/Messages'
import CoreChargeMessage from './CoreChargeMessage'
import {
    getOrderTotals,
} from './checkout-utils'
import {actionLinkStyle} from './checkout-styles'

const dashesStyle = css.resolve`
    ::after {
        display: inline-block;
        content: '----';
        letter-spacing: 3px;
    }
`

const OrderSummaryItem = ({item}) => {
    const isPricedWarranty = Boolean(item.warranty.warrantyPrice)

    return (
        <>
            <div className='container'>
                <div className='details-price'>
                    <div className='details'>
                        <div className='part-type'>{item.partTypeName}</div>
                        <div className='vehicle'>{item.modelYear} {item.makeName} {item.carlineName}</div>
                        <div className='stock-number'>Stock #&nbsp;&nbsp;&nbsp;{item.stockTicketNumber}</div>
                    </div>
                    <div className='price'>
                        <div>{formatDollars(item.itemPrice)}</div>
                        {item.isMakeOffer ? <div className='make-offer-notice'>Make-offer price</div> : null}
                    </div>
                </div>
                {
                    !item.corePrice
                        ? null
                        : (
                            <div className='details-price core-line'>
                                <div className='details'>
                                    <div className='core'>Core Deposit</div>
                                </div>
                                <div className='price'>
                                    <div>{formatDollars(item.corePrice)}</div>
                                </div>
                            </div>
                        )
                }
                {
                    !isPricedWarranty
                        ? null
                        : (
                            <div className='details-price warranty-line'>
                                <div className='details'>
                                    <div className='warranty'>{item.warranty.titleLong}</div>
                                </div>
                                <div className='price'>
                                    <div>{formatDollars(item.warranty.warrantyPrice)}</div>
                                </div>
                            </div>
                        )
                }
            </div>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .container {
                        border-bottom: 1px solid $c--blue--light-gray;
                        padding: 20px 0;

                        &:last-child {
                            border-bottom: 0;
                        }
                    }

                    .details-price {
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                    }

                    .core-line,
                    .warranty-line {
                        margin-top: 20px;
                    }

                    .core,
                    .warranty {
                        font-style: italic;
                    }

                    .details {
                        flex-grow: 1;
                    }

                    .part-type {
                        font-weight: $weight--bold;
                        margin-bottom: 5px;
                    }

                    .price {
                        flex: 0 0 100px;
                        text-align: right;
                    }

                    .make-offer-notice {
                        font-style: italic;
                        font-size: $size--small;
                    }
                `}
            </style>
        </>
    )
}

const OrderSummary = forwardRef(({disabled, onComplete, checkoutStore}, ref) => {
    if(!checkoutStore.info.checkoutToken) return null

    const checkoutInfo = checkoutStore.info
    const items = get('items', [], checkoutInfo.checkoutTokenPayload)

    const {orderTotal, shippingTotal, taxTotal, giftCardAmount, totalToCharge} = getOrderTotals(checkoutStore)

    const haveTaxes = checkoutInfo.salesTaxTokenPayload?.salesTaxTotal !== undefined

    const getShippingTotalDisplay = shippingTotal => {
        if(shippingTotal === null) return (<><span className={dashesStyle.className} />{dashesStyle.styles}</>)
        else if(shippingTotal === 0) return 'FREE'
        else return formatDollars(shippingTotal)
    }

    const hasCoreCharge = useMemo(() => {
        return items.some(x => Boolean(x.corePrice))
    }, [items])

    return (
        <>
            <div className='container'>
                <div className='headings'>
                    <div className={classnames('order-summary-heading', actionLinkStyle.className)}>Order Summary</div>
                    <div><Link href='/cart'><a className={actionLinkStyle.className}>Edit</a></Link></div>
                </div>
                <div className='items'>
                    {items.map(item => <OrderSummaryItem key={item.inventoryId} item={item} />)}
                </div>
                {
                    hasCoreCharge
                        ? <div className='core-message'><CoreChargeMessage /></div>
                        : null
                }
                <div ref={ref} className='totals'>
                    <div className='total-line sub-total'>
                        <div className='title'>Sub-total:</div>
                        <div className='price'>{formatDollars(orderTotal)}</div>
                    </div>
                    <div className='total-line'>
                        <div className='title'>Shipping & Handling</div>
                        <div className='price'>{getShippingTotalDisplay(shippingTotal)}</div>
                    </div>
                    <div className='total-line'>
                        <div className='title'>Estimated Tax</div>
                        <div className='price'>{haveTaxes ? formatDollars(taxTotal) : <span className={dashesStyle.className} />}</div>
                    </div>
                    {
                        !giftCardAmount
                            ? null
                            : (
                                <div className='total-line'>
                                    <div className='title'>Gift Card</div>
                                    <div className='price'>- {formatDollars(giftCardAmount)}</div>
                                </div>
                            )
                    }
                    <div className='total-line grand-total'>
                        <div className='title'>Total Due:</div>
                        <div className='price'>{formatDollars(totalToCharge)}</div>
                    </div>
                </div>
                <div className='complete-order'>
                    <button className='btn btn--yellow' disabled={disabled} onClick={() => !disabled && onComplete()}>Complete Order</button>
                </div>
            </div>
            {actionLinkStyle.styles}
            {dashesStyle.styles}
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .headings {
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-end;
                        padding-bottom: 12px;
                        border-bottom: 3px solid $c--blue--darker;
                    }

                    .totals {
                        background: white;
                        border-top: 3px solid $c--blue--darker;
                        padding: 15px 0;
                        border-radius: 0 0 6px 6px;
                    }

                    .total-line {
                        display: flex;
                        padding: 0 12px;
                        margin-bottom: 10px;

                        &:last-child {
                            margin-bottom: 0;
                        }

                        & .title {
                            flex-grow: 1;
                        }

                        & .price {
                            flex: 0 0 100px;
                            text-align: right;
                        }
                    }

                    .core-message {
                        font-style: italic;
                        margin-bottom: 15px;
                    }

                    .sub-total {
                        margin-bottom: 15px;

                        & .title {
                            font-weight: $weight--bold;
                        }
                    }

                    .grand-total {
                        border-top: 1px solid $c--blue--dark-gray;
                        margin-top: 15px;
                        padding-top: 15px;

                        & .title {
                            font-size: $size--medium;
                            font-weight: $weight--bold;
                        }

                        & .price {
                            font-weight: $weight--bold;
                        }
                    }

                    .complete-order {
                        margin-top: 30px;
                        text-align: center;
                    }
                `}
            </style>
        </>
    )
})

const SummaryLayout = forwardRef(({disabled, onComplete, checkoutStore, children}, ref) => {
    const config = useConfig()

    return (
        <>
            <div className='container'>
                <div className='left-side pad-left-gutter pad-right-gutter '>
                    <h1>Checkout</h1>

                    <div className='content'>
                        {children}
                    </div>
                </div>
                <div className='summary pad-left-gutter pad-right-gutter'>
                    <div className='summary-container'>
                        {
                            checkoutStore.complete.errors && checkoutStore.complete.errors.length
                                ? <Messages notice={[(<span key='callus'>If you continue to have issues checking out, please call a B&R sales representative at <a href={`tel:${config.contact.phoneFull}`}>{config.contact.phoneHuman}</a>.</span>)]} />
                                : null
                        }

                        <Messages error={checkoutStore.complete.errors} />

                        <OrderSummary ref={ref} disabled={disabled} onComplete={onComplete} checkoutStore={checkoutStore} />
                    </div>
                </div>
            </div>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .container {
                        background: $c--gray--bg; /* there's some white space between the two items on mobile. this covers it up. */

                        @media(--lg) {
                            display: flex;
                        }
                    }

                    .summary-container {
                        width: 400px;
                        margin: 0 auto;

                        @media(--xs) { width: 100%; margin: 0; }
                        @media(--lg) { width: 350px; margin: 0; }
                        @media(--xl) { width: 400px; }
                    }

                    .left-side {
                        /* can't use shorthand padding or will override pad-left-gutter */
                        padding-top: 40px;
                        flex-grow: 1;

                        @media(--lg) {
                            padding-right: 30px; /* override the pad-right-gutter */
                        }
                    }

                    .summary {
                        background: $c--blue--lightest;
                        /* can't use shorthand padding or will override pad-right-gutter */
                        padding-top: 40px;
                        padding-bottom: 40px;

                        @media(--lg) {
                            padding-left: 30px; /* override the pad-left-gutter */
                        }
                    }
                `}
            </style>
        </>
    )
})

export default SummaryLayout
